import { type Database } from "@fitness-app/supabase";

import { type AutomationSettings } from "./Automation";

export type CheckoutVariantEntity = Database["public"]["Tables"]["checkout_variant"]["Row"];

export type VariantPrice = string;

export interface DownsellPrice {
  priceId: string;
}

export enum CheckoutVariantStatus {
  Active = "active",
  Inactive = "inactive",
  Archived = "archived",
}

export enum CheckoutVariantTarget {
  All = "all",
  New = "new",
  Renewal = "renewal",
}

export interface PricingFeature {
  name: string;
  value?: string;
  originalPrice?: string;
  isFree?: boolean;
  hideCheck?: boolean; // Hides the checkmark icon
  isGratis?: boolean; // Legacy support
  bolded?: boolean;
  striketrough?: boolean;
  /**
   * Kolumna tej pozycji nie ma: układ `optionsTable` stawia ✕ i przygasza tekst.
   * Pozycje łączy miejsce w tablicy, więc brak trzeba zapisać wierszem, a nie pominięciem.
   */
  unavailable?: boolean;
}

/** Linia „albo …” pod ratą w kolumnie układu `optionsTable`, np. „6 017 zł jednorazowo za 13 miesięcy”. */
export interface PricingPaymentOption {
  label: string;
  /** Dopisek pod linią, np. „oszczędzasz 1 094 zł”. */
  savings?: string;
}

export interface PricingPlan {
  title: string;
  best?: boolean;
  subtitle?: string;
  regularPrice: string;
  promoPrice: string;
  monthlyInfo: string;
  oneTimeInfo?: string;
  features: PricingFeature[];
  bonuses: PricingFeature[];
  /**
   * Pola poniżej czyta wyłącznie szablon `hollywood2026`. Starsza tabela
   * (`PricingRealTable`) je ignoruje, więc dopisanie ich nie zmienia wyglądu
   * istniejących wariantów.
   */
  emoji?: string;
  /** Badge nad ceną, np. „Najczęściej wybierany”. `best` nadal steruje wyróżnieniem karty. */
  badge?: string;
  /** Jedno zdanie o tym, dla kogo jest ten pakiet. */
  description?: string;
  /** Kotwica wartości — suma wyceny elementów programu, np. „12 480 zł”. */
  totalValue?: string;
  /** Podpis pod kotwicą, np. „tyle kosztowałoby to osobno”. */
  totalValueCaption?: string;
  /** Nadpisuje etykietę przycisku prowadzącego do koszyka. */
  ctaLabel?: string;
  /** Dopisek pod przyciskiem, np. informacja o gwarancji. */
  ctaCaption?: string;
  /**
   * Cena z wariantu, którą przycisk pakietu ma zaznaczyć w koszyku (id ze Stripe,
   * `price_...`). Bez tego kliknięcie tylko przewija do formularza i zostaje
   * zaznaczona cena domyślna — czyli opis pakietu mógł mówić co innego niż
   * kwota, którą klientka widziała po przewinięciu.
   */
  priceId?: string;
  /** Inne sposoby zapłaty za ten pakiet, pokazywane pod ratą w układzie `optionsTable`. */
  paymentOptions?: PricingPaymentOption[];
}

/** Pojedyncza funkcja aplikacji pokazywana ze zrzutem ekranu w szablonie `hollywood2026`. */
export interface AppShowcaseFeature {
  title: string;
  description: string;
  /** Etykieta nad tytułem, np. „Nowość” albo „AI”. */
  badge?: string;
  /** Ścieżka do zrzutu ekranu, proporcje 9:19.5 (ramka telefonu). */
  screenshot?: string;
  /** Tekst alternatywny zrzutu — domyślnie tytuł funkcji. */
  screenshotAlt?: string;
  /**
   * Identyfikator interaktywnej makiety ekranu. Gdy ustawiony i znany, ramka
   * telefonu renderuje klikalną makietę zamiast zrzutu; `screenshot` zostaje
   * jako zapas, więc nieznana wartość nie psuje strony.
   */
  demo?: AppShowcaseDemoId;
  /** Punkty wypunktowane pod opisem. */
  highlights?: string[];
}

/** Ekrany aplikacji dostępne jako interaktywne makiety w szablonie `hollywood2026`. */
export type AppShowcaseDemoId =
  | "home"
  | "dietitian-chat"
  | "meal-generation"
  | "meal-plan"
  | "training-plan"
  | "nutrition-report"
  | "calorie-calculator"
  | "challenges"
  | "habits"
  | "health-activity";

/** Drobniejsza funkcja pokazywana w siatce pod głównymi funkcjami — bez zrzutu ekranu. */
export interface AppShowcaseExtra {
  title: string;
  description: string;
  emoji?: string;
}

export interface AppShowcaseConfig {
  title?: string;
  description?: string;
  features: AppShowcaseFeature[];
  extrasTitle?: string;
  extras?: AppShowcaseExtra[];
}

export interface CheckoutFaqEntry {
  question: string;
  answer: string;
}

export interface CheckoutFaqConfig {
  title?: string;
  entries: CheckoutFaqEntry[];
}

/**
 * Który układ strony wokół koszyka wyrenderować.
 * `classic` to dotychczasowy layout i pozostaje domyślny — brak wartości znaczy `classic`.
 */
export type CheckoutLandingTemplate = "classic" | "hollywood2026" | "hollywoodBlackFriday";

/**
 * Szablony stojące na układzie `hollywood2026`. `hollywoodBlackFriday` to ten sam układ sekcji,
 * tylko zawsze w ciemnym motywie, ze zwartym koszykiem i hero ze zdjęciem kampanii.
 */
export const isHollywoodLandingTemplate = (template: CheckoutLandingTemplate | undefined) =>
  template === "hollywood2026" || template === "hollywoodBlackFriday";

/**
 * Sekcje szablonu `hollywood2026`, którym wariant może wskazać miejsce na stronie.
 * Kolejność tej listy jest domyślną kolejnością renderowania (wariant zmienia ją `sectionOrder`) — w obrębie grupy nad koszykiem
 * i (osobno) pod koszykiem. Sekcja z wideo promocyjnym nie jest tu wymieniona, bo jej
 * miejscem steruje `videoOnTop`.
 */
export const HOLLYWOOD_2026_SECTIONS = [
  "rating",
  "team",
  "appShowcase",
  "pricing",
  "guarantee",
  "transformations",
  "faq",
] as const;

export type Hollywood2026SectionId = (typeof HOLLYWOOD_2026_SECTIONS)[number];

/**
 * Sekcje w kolejności z wariantu (`sectionOrder`). Nieznane i powtórzone wpisy odpadają,
 * a sekcje, których wariant nie wymienia (np. dodane później), dochodzą na końcu w kolejności
 * domyślnej, więc żadna nie znika przez starszą konfigurację.
 */
export const orderHollywood2026Sections = (order: readonly string[] | undefined): Hollywood2026SectionId[] => {
  const known = new Set<string>(HOLLYWOOD_2026_SECTIONS);
  const listed = [...new Set(order ?? [])].filter((section): section is Hollywood2026SectionId => known.has(section));

  return [...listed, ...HOLLYWOOD_2026_SECTIONS.filter((section) => !listed.includes(section))];
};

/** Gdzie sekcja ma stanąć względem formularza koszyka. */
export type CheckoutSectionPlacement = "above" | "below" | "hidden";

/**
 * Układ, który szablon miał, zanim dało się nim sterować — i który dostaje każdy
 * wariant bez `sectionPlacement`. Dzięki temu dopisanie pola nie rusza istniejących koszyków.
 */
export const DEFAULT_HOLLYWOOD_2026_SECTION_PLACEMENT: Record<Hollywood2026SectionId, CheckoutSectionPlacement> = {
  rating: "above",
  team: "above",
  appShowcase: "above",
  pricing: "above",
  guarantee: "above",
  transformations: "below",
  faq: "below",
};

/** Miejsce koszyka na liście sekcji w edytorach: co nad nim, staje nad koszykiem, co pod nim, pod. */
export const HOLLYWOOD_CART_MARKER = "cart" as const;

export type HollywoodSectionListItem = Hollywood2026SectionId | typeof HOLLYWOOD_CART_MARKER;

type SectionLayoutConfig = Pick<PageConfiguration, "sectionOrder" | "sectionPlacement" | "hiddenSections">;

/** Strona koszyka. Dawne `sectionPlacement: "hidden"` nie mówi o stronie, więc bierze domyślną. */
const sideOf = (config: SectionLayoutConfig | null | undefined, section: Hollywood2026SectionId) => {
  const placement = config?.sectionPlacement?.[section];

  return placement === "above" || placement === "below" ? placement : DEFAULT_HOLLYWOOD_2026_SECTION_PLACEMENT[section];
};

export const isHollywoodSectionHidden = (
  config: SectionLayoutConfig | null | undefined,
  section: Hollywood2026SectionId,
) => Boolean(config?.hiddenSections?.includes(section)) || config?.sectionPlacement?.[section] === "hidden";

/** Gdzie sekcja staje na stronie. */
export const hollywoodSectionPlacement = (
  config: SectionLayoutConfig | null | undefined,
  section: Hollywood2026SectionId,
): CheckoutSectionPlacement => (isHollywoodSectionHidden(config, section) ? "hidden" : sideOf(config, section));

/** Lista do przeciągania w edytorach: sekcje nad koszykiem, znacznik koszyka, sekcje pod koszykiem. */
export const hollywoodSectionList = (config: SectionLayoutConfig | null | undefined): HollywoodSectionListItem[] => {
  const order = orderHollywood2026Sections(config?.sectionOrder);

  return [
    ...order.filter((section) => sideOf(config, section) === "above"),
    HOLLYWOOD_CART_MARKER,
    ...order.filter((section) => sideOf(config, section) === "below"),
  ];
};

export const hollywoodHiddenSections = (config: SectionLayoutConfig | null | undefined) =>
  HOLLYWOOD_2026_SECTIONS.filter((section) => isHollywoodSectionHidden(config, section));

/**
 * Konfiguracja z listy edytora: kolejność bez znacznika, strona każdej sekcji z jej miejsca
 * względem znacznika koszyka, widoczność z przełączników.
 */
export const hollywoodSectionsFromList = (
  list: readonly HollywoodSectionListItem[],
  hidden: readonly Hollywood2026SectionId[],
): Required<SectionLayoutConfig> => {
  const cartIndex = list.indexOf(HOLLYWOOD_CART_MARKER);
  const sections = list.filter((item): item is Hollywood2026SectionId => item !== HOLLYWOOD_CART_MARKER);

  return {
    sectionOrder: sections,
    sectionPlacement: Object.fromEntries(
      sections.map((section) => [section, cartIndex >= 0 && list.indexOf(section) > cartIndex ? "below" : "above"]),
    ),
    hiddenSections: sections.filter((section) => hidden.includes(section)),
  };
};

/**
 * Jak pokazać cennik w szablonie `hollywood2026`.
 * `cards` — karty pakietów (domyślne).
 * `comparison` — dwukolumnowa oferta zestawiona wiersz w wiersz, jak w starej
 * `PricingRealTable`: pozycje łączy pozycja w tablicy, a nie nazwa, więc każda kolumna
 * może mieć własną nazwę i własną wycenę tej samej pozycji (albo nie mieć jej wcale).
 * `optionsTable` — jedna karta z kolumnami: rata jako główna liczba, pod nią linie „albo …”
 * z innymi sposobami zapłaty (`paymentOptions`), wiersze ✓ / ✕ (`unavailable`) i przycisk pod kolumną.
 */
export type CheckoutPricingLayout = "cards" | "comparison" | "optionsTable";

export interface CustomVariantConsent {
  label: string;
  url: string | null;
  required: boolean;
  name: string;
  applyToProducts: true;
  filteredByPriceId?: null | string[];
}

export enum RadixColor {
  Tomato = "tomato",
  Red = "red",
  Ruby = "ruby",
  Crimson = "crimson",
  Pink = "pink",
  Plum = "plum",
  Purple = "purple",
  Violet = "violet",
  Iris = "iris",
  Indigo = "indigo",
  Blue = "blue",
  Cyan = "cyan",
  Teal = "teal",
  Jade = "jade",
  Green = "green",
  Grass = "grass",
  Brown = "brown",
  Orange = "orange",
  Sky = "sky",
  Mint = "mint",
  Lime = "lime",
  Yellow = "yellow",
  Amber = "amber",
  Gold = "gold",
  Bronze = "bronze",
  Gray = "gray",
}

export const RADIX_COLOR_MAP: Record<RadixColor, string> = {
  [RadixColor.Tomato]: "#e54d2e",
  [RadixColor.Red]: "#e5484d",
  [RadixColor.Ruby]: "#e54666",
  [RadixColor.Crimson]: "#e93d82",
  [RadixColor.Pink]: "#d6409f",
  [RadixColor.Orange]: "#f76b15",
  [RadixColor.Lime]: "#bdee63",
  [RadixColor.Yellow]: "#ffe629",
  [RadixColor.Amber]: "#ffc53d",
  [RadixColor.Plum]: "#ab4aba",
  [RadixColor.Purple]: "#8e4ec6",
  [RadixColor.Violet]: "#6e56cf",
  [RadixColor.Iris]: "#5b5bd6",
  [RadixColor.Indigo]: "#3e63dd",
  [RadixColor.Blue]: "#0090ff",
  [RadixColor.Cyan]: "#00a2c7",
  [RadixColor.Sky]: "#7ce2fe",
  [RadixColor.Mint]: "#86ead4",
  [RadixColor.Teal]: "#12a594",
  [RadixColor.Jade]: "#29a383",
  [RadixColor.Green]: "#30a46c",
  [RadixColor.Grass]: "#46a758",
  [RadixColor.Brown]: "#ad7f58",
  [RadixColor.Bronze]: "#a18072",
  [RadixColor.Gold]: "#978365",
  [RadixColor.Gray]: "#8d8d8d",
};

/** Nagłówek strony szablonu `hollywood2026`, np. „Gdzie będziesz za 12 miesięcy?” na przedłużeniach. */
export interface CheckoutPageHeadingConfig {
  title: string;
  description?: string;
}

/** Baner nad cennikiem szablonu `hollywood2026`: tytuł i krótka lista argumentów. */
export interface CheckoutPricingHighlightCard {
  /** Wyróżnione złotem, np. „430 zł miesięcznie”. */
  title: string;
  description?: string;
}

export interface CheckoutPricingHighlightConfig {
  title: string;
  points?: string[];
  /**
   * Karty pod tytułem (np. „430 zł miesięcznie” / „zamiast 497 zł w ratach”). Gdy są, baner
   * pokazuje je zamiast listy `points`.
   */
  cards?: CheckoutPricingHighlightCard[];
}

/** Hero szablonu `hollywoodBlackFriday`: zdjęcie kampanii jest w kodzie, tekst w wariancie. */
export interface CheckoutCampaignHeroConfig {
  /** Brak wartości = „Black Hollywood 2026”. */
  title?: string;
  lead?: string;
}

/** Motyw, w którym strona otwiera się klientce, zanim sama go przełączy. Brak wartości = ustawienie systemu. */
export type CheckoutColorScheme = "light" | "dark";

export interface GuaranteeBannerConfig {
  title: string;
  /** Zdanie pod tytułem. Puste = sam tytuł i punkty. */
  description?: string;
  benefits: string[];
  limitedAvailability?: {
    icon?: string;
    text: string;
  };
}

export interface PageConfiguration {
  pricing?: PricingPlan[];
  vipPricing?: PricingPlan[];
  productNameOverride?: string;
  productImageOverride?: string;
  productDescriptionOverride?: string;
  customConsents?: CustomVariantConsent[];
  pricingDisabled?: boolean;
  richPriceCards?: boolean;
  timer?:
    | false
    | {
        title: string;
        description: string;
        startInfo: string;
        titleColor?: RadixColor;
        showSeatsLeft?: boolean;
        hideCountdown?: boolean;
      };
  promoVideo?: {
    title: string;
    videoId: string;
    description?: string;
    hideIcons?: boolean;
  };
  enableLandingPage?: boolean;
  /**
   * Ukrywa sekcję hero na górze strony — we wszystkich szablonach. Licznika ani wideo to nie
   * dotyczy. W szablonach `hollywood2026` i `hollywoodBlackFriday` hero i licznik są niezależne:
   * licznik wyłącza `timer: false`, więc strona może mieć oba, jedno albo żadne.
   */
  hideHero?: boolean;
  videoOnTop?: boolean;
  renewalFallback?: string;
  guaranteeBanner?: GuaranteeBannerConfig;
  /** Brak wartości = `classic`, czyli dotychczasowy układ strony. */
  landingTemplate?: CheckoutLandingTemplate;
  /**
   * Miejsce poszczególnych sekcji szablonu `hollywood2026` względem koszyka.
   * Brak wpisu dla sekcji = wartość z `DEFAULT_HOLLYWOOD_2026_SECTION_PLACEMENT`.
   */
  sectionPlacement?: Partial<Record<Hollywood2026SectionId, CheckoutSectionPlacement>>;
  /**
   * Kolejność sekcji szablonów Hollywood, przestawiana przeciąganiem w edytorach. Dotyczy obu
   * grup (nad i pod koszykiem). Brak wartości = kolejność `HOLLYWOOD_2026_SECTIONS`.
   */
  sectionOrder?: Hollywood2026SectionId[];
  /** Sekcje wyłączone przełącznikiem w edytorach. Zachowują miejsce na liście na wypadek włączenia. */
  hiddenSections?: Hollywood2026SectionId[];
  /** Postać cennika w szablonie `hollywood2026`. Brak wartości = `cards`. */
  pricingLayout?: CheckoutPricingLayout;
  /** Nagłówek sekcji cennika w szablonie `hollywood2026`. Brak wartości = domyślny nagłówek układu. */
  pricingTitle?: string;
  /** Zdanie pod nagłówkiem cennika w szablonie `hollywood2026`. */
  pricingDescription?: string;
  /** Napis na pasie bonusów w układzie `comparison`. Brak wartości = „Bonusy”. */
  pricingBonusesTitle?: string;
  /** Baner między nagłówkiem cennika a cennikiem w szablonie `hollywood2026`. */
  pricingHighlight?: CheckoutPricingHighlightConfig;
  /**
   * Nagłówek strony w szablonie `hollywood2026`, pod licznikiem albo hero. Przedłużenia nie mają
   * hero, więc to jedyne miejsce na zdanie, które otwiera stronę.
   */
  pageHeading?: CheckoutPageHeadingConfig;
  /** Tekst hero w szablonie `hollywoodBlackFriday`. */
  campaignHero?: CheckoutCampaignHeroConfig;
  /** Domyślny motyw strony koszyka. Przełącznik motywu dalej działa, a wybór klientki wygrywa. */
  defaultColorScheme?: CheckoutColorScheme;
  /** Sekcja z funkcjami aplikacji — renderowana tylko w szablonie `hollywood2026`. */
  appShowcase?: AppShowcaseConfig;
  /** FAQ pod koszykiem — renderowane tylko w szablonie `hollywood2026`. */
  faq?: CheckoutFaqConfig;
}

export interface CheckoutVariantPriceStats {
  /** Suma faktycznie pobranych kwot — pierwszych płatności. */
  amount: number;
  /** Suma wartości kontraktów: pierwsze płatności + pozostałe raty po rabatach. */
  projected: number;
  transactions: number;
  /**
   * Ile z tych transakcji poszło za 0 zł (kod 100%, gratis, korekta). Liczone dopiero
   * z zamówień — starsze wpisy z licznika w `checkout_variant` tego nie niosą, stąd opcjonalne.
   */
  freeTransactions?: number;
  /** Ile rabaty zdjęły z ceny katalogowej, w groszach. */
  discountAmount?: number;
}

/**
 * Starsze warianty trzymają w `pricesStats` samą sumę kwot. Nie da się z niej
 * odtworzyć liczby transakcji (dzielenie przez cenę katalogową rozjeżdża się przy
 * każdym rabacie), więc dla takich wpisów licznik zostaje zerowy.
 */
export const normalizeCheckoutVariantPriceStats = (
  entry: CheckoutVariantPriceStats | number | undefined,
): CheckoutVariantPriceStats => {
  if (typeof entry === "number") {
    return { amount: entry, projected: entry, transactions: 0 };
  }

  return entry ?? { amount: 0, projected: 0, transactions: 0 };
};

export interface CheckoutVariant extends Omit<
  CheckoutVariantEntity,
  "downsellPrices" | "metadata" | "prices" | "pricesStats" | "pageConfiguration" | "cartSettings"
> {
  downsellPrices: DownsellPrice[] | null;
  prices: VariantPrice[];
  pricesStats: Record<string, CheckoutVariantPriceStats | number>;
  metadata:
    | (Record<string, string | number | null | boolean | string[] | number[]> & {
        allowManualPaymentForPrices?: (string | "all")[];
        /**
         * Cena zaznaczona w koszyku przedłużenia, gdy nie wskazuje jej adres (`?priceId`). Bez tego
         * koszyk przedłużeń zaznacza najdroższą cenę wariantu.
         */
        defaultPriceId?: string;
      })
    | null;
  status: CheckoutVariantStatus;
  target: CheckoutVariantTarget;
  pageConfiguration: PageConfiguration | null;
  /**
   * Variant-level override of the product's `AutomationSettings`. When present, these values
   * take precedence over the product's automation settings (and the funnel's `cartSettings`)
   * when composing the cart's product info. See `ProductCheckoutClientService.composeProductInfo`.
   */
  cartSettings?: Partial<AutomationSettings> | null;
}
